import {
  type FC,
  type RefObject,
  type SyntheticEvent,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { observer } from 'mobx-react-lite';
import { ArrowDown, Check, ChevronRight, Minus, X } from 'lucide-react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { useMessagingStore } from '@/app/lib/hooks';
import { messages } from '../localization/messages.ts';
import type { ActivityState, ToolActivity } from './NarrationStore.ts';
import { activityDisplayState, activityGroups, type ActivityGroup } from './activity-context.ts';
import type { TurnEntry } from './turn-index.ts';
import { splitAttachmentMarker } from '../files/attachment-marker.ts';
import { StageProgressLine } from './StageProgressLine.tsx';

const DISCLOSURE_CLASS =
  'flex min-h-8 w-fit cursor-pointer list-none items-center rounded-sm text-xs text-muted-foreground underline decoration-border underline-offset-4 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const STATE_MESSAGES: Record<ActivityState, MessageDescriptor> = {
  preparing: messages.activityPreparingState,
  running: messages.activityRunning,
  returned: messages.activityReturned,
  failed: messages.activityFailed,
  unconfirmed: messages.activityUnconfirmed,
};

const OPERATION_MESSAGES: Record<ToolActivity['kind'], MessageDescriptor> = {
  surface: messages.activitySurface,
  research: messages.activityResearch,
  search: messages.activitySearch,
  image: messages.activityImage,
  service: messages.activityService,
  computation: messages.activityComputation,
  tool: messages.activityTool,
};

const CURRENT_MESSAGES: Record<ToolActivity['kind'], MessageDescriptor> = {
  surface: messages.narrationScreenReady,
  research: messages.activitySearchWorking,
  search: messages.activitySearchWorking,
  image: messages.activityImageWorking,
  service: messages.activityServiceWorking,
  computation: messages.activityComputationWorking,
  tool: messages.activityWaiting,
};

const RETURNED_MESSAGES: Record<ToolActivity['kind'], MessageDescriptor> = {
  surface: messages.activityPageUpdated,
  research: messages.activitySearched,
  search: messages.activitySearched,
  image: messages.activityReturned,
  service: messages.activityServiceReturned,
  computation: messages.activityCalculationReturned,
  tool: messages.activityReturned,
};

const ActivityMark: FC<{ state: ActivityState }> = ({ state }) => {
  if (state === 'preparing' || state === 'running') {
    return (
      <span className="stage-activity-mark stage-activity-working" aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
    );
  }
  let Icon = Check;
  if (state === 'failed') {
    Icon = X;
  } else if (state === 'unconfirmed') {
    Icon = Minus;
  }
  return (
    <span className="stage-activity-mark" aria-hidden="true">
      <Icon className="h-3.5 w-3.5" />
    </span>
  );
};

const ActivitySources: FC<{ sources: NonNullable<ToolActivity['sources']> }> = ({ sources }) => (
  <ul className="space-y-3">
    {sources.map((source) => (
      <li key={source.url}>
        <a
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block rounded-sm text-sm leading-5 hover:underline hover:underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="block break-words">{source.title}</span>
          <span className="mt-1 block truncate text-xs text-muted-foreground">
            {new URL(source.url).hostname}
          </span>
        </a>
      </li>
    ))}
  </ul>
);

const ActivityImage: FC<{
  url: string;
  onToggle: (event: SyntheticEvent<HTMLDetailsElement>) => void;
}> = ({ url, onToggle }) => {
  const intl = useIntl();
  const [failedImage, setFailedImage] = useState<string>();
  if (failedImage === url) {
    return (
      <p className="mt-3 text-xs leading-5 text-muted-foreground">
        {intl.formatMessage(messages.activityImageUnavailable)}
      </p>
    );
  }
  return (
    <details className="mt-3" onToggle={onToggle}>
      <summary className="flex cursor-pointer list-none items-center gap-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <img
          src={url}
          alt=""
          className="h-16 w-24 shrink-0 rounded-md object-cover"
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailedImage(url)}
        />
        <span className="text-xs leading-5 text-muted-foreground underline decoration-border underline-offset-4">
          {intl.formatMessage(messages.activityImagePreview)}
        </span>
      </summary>
      <img
        src={url}
        alt={intl.formatMessage(messages.activityGeneratedImage)}
        className="mt-3 max-h-64 w-full rounded-lg object-contain"
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailedImage(url)}
      />
    </details>
  );
};

export const StageActivityTrigger: FC<{
  open: boolean;
  available: boolean;
  status?: string;
  detail?: string;
  working: boolean;
  triggerRef: RefObject<HTMLButtonElement>;
  onToggle: () => void;
}> = ({ open, available, status, detail, working, triggerRef, onToggle }) => {
  const intl = useIntl();
  if (!available) {
    return <StageProgressLine text={status} detail={detail} working={working} />;
  }
  return (
    <>
      <StageProgressLine
        text={status}
        detail={detail}
        working={working}
        className="stage-activity-passive-status"
      />
      <button
        ref={triggerRef}
        type="button"
        aria-label={intl.formatMessage(messages.activity)}
        aria-expanded={open}
        aria-controls="stage-activity"
        onClick={onToggle}
        className="stage-activity-trigger group flex max-w-full items-center gap-2 rounded-md px-2 py-1 text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="truncate" role="status">
          {status || intl.formatMessage(messages.activity)}
        </span>
        <ChevronRight
          className="h-4 w-4 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none rtl:rotate-180"
          aria-hidden="true"
        />
      </button>
    </>
  );
};

const ActivityOperation: FC<{
  tool: ToolActivity;
  canLocate: boolean;
  onInspect: () => void;
  onViewSurface: (surfaceId: string) => void;
}> = observer(({ tool, canLocate, onInspect, onViewSurface }) => {
  const intl = useIntl();
  const subject = tool.subjects?.at(-1);
  const previousSubjects = tool.subjects?.slice(0, -1) ?? [];
  const note = tool.commentary?.kind === 'authored' ? tool.commentary : undefined;
  const sources = tool.sources ?? [];
  const state = activityDisplayState(tool);
  const active = state === 'preparing' || state === 'running';
  const exceptional = state === 'failed' || state === 'unconfirmed';
  let heading = OPERATION_MESSAGES[tool.kind];
  if (active) {
    heading = CURRENT_MESSAGES[tool.kind];
  } else if (tool.imageUrl) {
    heading = messages.activityGeneratedImage;
  } else if (state === 'returned') {
    heading = RETURNED_MESSAGES[tool.kind];
  }
  const onToggle = (event: SyntheticEvent<HTMLDetailsElement>) => {
    if (event.currentTarget.open) {
      onInspect();
    }
  };
  return (
    <li data-activity-state={state} className="stage-activity-step">
      <ActivityMark state={state} />
      <div className="min-w-0 pb-7">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <h3 className="break-words text-sm font-medium leading-6">
            {intl.formatMessage(heading)}
          </h3>
          {canLocate && state === 'returned' ? (
            <button
              type="button"
              className="min-h-8 rounded-sm text-xs underline decoration-border underline-offset-4 hover:decoration-current focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => {
                if (tool.surfaceId) {
                  onViewSurface(tool.surfaceId);
                }
              }}
            >
              {intl.formatMessage(messages.activityViewPage)}
            </button>
          ) : null}
        </div>
        {subject ? (
          <p dir="auto" className="mt-1 break-words text-sm leading-relaxed text-muted-foreground">
            {subject}
          </p>
        ) : null}
        {exceptional ? (
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            {intl.formatMessage(STATE_MESSAGES[state])}
          </p>
        ) : null}
        {note ? (
          <details className="group/note mt-2" onToggle={onToggle}>
            <summary className="cursor-pointer list-none rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <span
                dir="auto"
                className="line-clamp-2 break-words text-sm leading-relaxed text-muted-foreground group-open/note:hidden"
              >
                {note.text}
              </span>
              <span className={DISCLOSURE_CLASS}>
                {intl.formatMessage(messages.activityCommentary)}
              </span>
            </summary>
            <blockquote
              dir="auto"
              className="mt-2 break-words text-sm leading-relaxed text-muted-foreground"
            >
              {note.text}
            </blockquote>
          </details>
        ) : null}
        {sources.length > 0 ? (
          <div className="mt-3">
            <ActivitySources sources={sources.slice(0, 2)} />
            {sources.length > 2 ? (
              <details className="mt-2" onToggle={onToggle}>
                <summary className={DISCLOSURE_CLASS}>
                  {intl.formatMessage(messages.activityMoreSources)}
                </summary>
                <div className="mt-2">
                  <ActivitySources sources={sources.slice(2)} />
                </div>
              </details>
            ) : null}
          </div>
        ) : null}
        {tool.imageUrl ? <ActivityImage url={tool.imageUrl} onToggle={onToggle} /> : null}
        {previousSubjects.length > 0 ? (
          <details className="mt-2" onToggle={onToggle}>
            <summary className={DISCLOSURE_CLASS}>
              {intl.formatMessage(messages.activitySearches)}
            </summary>
            <ul className="mt-2 space-y-2 text-sm text-muted-foreground">
              {previousSubjects.map((text) => (
                <li key={text} className="break-words leading-relaxed">
                  {text}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </div>
    </li>
  );
});

const ActivityRequest: FC<{
  group: ActivityGroup;
  selected: boolean;
  preserveExpanded: boolean;
  navigableSurfaceIds: string[];
  onInspect: () => void;
  onViewSurface: (surfaceId: string) => void;
}> = observer(
  ({ group, selected, preserveExpanded, navigableSurfaceIds, onInspect, onViewSurface }) => {
    const intl = useIntl();
    const [userExpanded, setUserExpanded] = useState<boolean>();
    const previousSelected = useRef(selected);
    if (selected !== previousSelected.current) {
      previousSelected.current = selected;
      if (selected) {
        setUserExpanded(undefined);
      } else if (preserveExpanded && userExpanded !== false) {
        setUserExpanded(true);
      }
    }
    const expanded = userExpanded ?? selected;
    const { run } = group;
    const tools = run?.tools ?? [];
    const hasActiveTool = tools.some(
      (tool) => tool.state === 'preparing' || tool.state === 'running',
    );
    const label = group.home
      ? intl.formatMessage(messages.home)
      : splitAttachmentMarker(group.request ?? '').text ||
        intl.formatMessage(messages.activityTool);
    const inspect = () => {
      setUserExpanded(true);
      onInspect();
    };

    return (
      <details
        open={expanded}
        data-activity-response={group.responseId}
        className="group/request py-2"
      >
        <summary
          className="flex min-h-10 cursor-pointer list-none items-start gap-2 rounded-sm py-2 text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={(event) => {
            event.preventDefault();
            setUserExpanded(!expanded);
            onInspect();
          }}
        >
          <ChevronRight
            className="mt-0.5 h-4 w-4 shrink-0 transition-transform group-open/request:rotate-90 motion-reduce:transition-none rtl:rotate-180"
            aria-hidden="true"
          />
          <span
            dir="auto"
            className="min-w-0 break-words leading-5 line-clamp-2 group-open/request:line-clamp-none"
          >
            {label}
          </span>
        </summary>
        <div className="pt-3">
          {group.sharedResponse ? (
            <p className="mb-4 text-xs leading-relaxed text-muted-foreground">
              {intl.formatMessage(messages.activitySharedResponse)}
            </p>
          ) : null}
          {run && run.state !== 'running' && run.state !== 'finished' ? (
            <p className="mb-4 text-sm text-muted-foreground">
              {intl.formatMessage(messages.activityInterrupted)}
            </p>
          ) : null}
          {tools.length === 0 && run?.state !== 'running' ? (
            <p className="pb-4 text-sm text-muted-foreground">
              {intl.formatMessage(messages.activityUnavailable)}
            </p>
          ) : null}
          <ol className="stage-activity-trail" aria-label={label}>
            {tools.map((tool) => (
              <ActivityOperation
                key={tool.toolCallId}
                tool={tool}
                canLocate={
                  tool.surfaceId !== undefined && navigableSurfaceIds.includes(tool.surfaceId)
                }
                onInspect={inspect}
                onViewSurface={onViewSurface}
              />
            ))}
            {run?.state === 'running' && !hasActiveTool ? (
              <li className="stage-activity-step" data-activity-state="running">
                <ActivityMark state="running" />
                <p className="pb-7 text-sm font-medium leading-6" role="status">
                  {intl.formatMessage(messages.activityWaiting)}
                </p>
              </li>
            ) : null}
          </ol>
          {run?.truncated ? (
            <p className="pb-4 text-sm text-muted-foreground">
              {intl.formatMessage(messages.activityTruncated)}
            </p>
          ) : null}
        </div>
      </details>
    );
  },
);

export const StageActivity: FC<{
  open: boolean;
  responseId?: string;
  request?: string;
  turns: readonly TurnEntry[];
  sharedResponse: boolean;
  viewingHistory: boolean;
  liveStatus?: string;
  triggerRef: RefObject<HTMLButtonElement>;
  onClose: () => void;
  onViewLive: () => void;
  onViewSurface: (surfaceId: string) => void;
  navigableSurfaceIds: string[];
}> = observer(
  ({
    open,
    responseId,
    request,
    turns,
    sharedResponse,
    viewingHistory,
    liveStatus,
    triggerRef,
    onClose,
    onViewLive,
    onViewSurface,
    navigableSurfaceIds,
  }) => {
    const store = useMessagingStore();
    const intl = useIntl();
    const panelRef = useRef<HTMLElement>(null);
    const headingRef = useRef<HTMLHeadingElement>(null);
    const scrollRef = useRef<HTMLElement>(null);
    const following = useRef(true);
    const inspecting = useRef(false);
    const previousSelection = useRef<string>();
    const [unseen, setUnseen] = useState(false);
    const wasOpened = useRef(false);
    if (open) wasOpened.current = true;
    const run = responseId ? store.narration.runs.get(responseId) : undefined;
    const tools = run?.tools ?? [];
    const groups = wasOpened.current
      ? activityGroups(store.narration.runs, turns, { responseId, request, sharedResponse })
      : [];
    const changeKey = JSON.stringify(groups.map((group) => group.run));

    useLayoutEffect(() => {
      if (open) {
        headingRef.current?.focus({ preventScroll: true });
      }
    }, [open]);

    useLayoutEffect(() => {
      const scroller = scrollRef.current;
      if (!scroller) {
        return;
      }
      if (!open) {
        return;
      }
      const selection = viewingHistory ? responseId : undefined;
      if (selection && selection !== previousSelection.current) {
        const group = [...scroller.querySelectorAll<HTMLElement>('[data-activity-response]')].find(
          (element) => element.dataset.activityResponse === selection,
        );
        if (group) {
          scroller.scrollTop +=
            group.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
        }
        following.current = false;
        inspecting.current = true;
      }
      previousSelection.current = selection;
      if (following.current) {
        scroller.scrollTop = scroller.scrollHeight;
        setUnseen(false);
      } else {
        setUnseen(true);
      }
    }, [responseId, viewingHistory, open, changeKey]);

    const close = () => {
      if (panelRef.current?.contains(document.activeElement)) {
        triggerRef.current?.focus({ preventScroll: true });
      }
      onClose();
    };
    const followLatest = () => {
      const el = scrollRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
      following.current = true;
      inspecting.current = false;
      setUnseen(false);
    };

    return (
      <aside
        ref={panelRef}
        id="stage-activity"
        hidden={!open}
        aria-labelledby="stage-activity-heading"
        aria-describedby="stage-activity-request"
        className="stage-activity-panel"
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !event.defaultPrevented) {
            event.stopPropagation();
            close();
          }
        }}
      >
        <div className="stage-activity-header flex h-12 shrink-0 items-center justify-between gap-3 px-6">
          <h2
            ref={headingRef}
            id="stage-activity-heading"
            tabIndex={-1}
            className="flex-1 text-base font-medium outline-none"
          >
            {intl.formatMessage(messages.activity)}
          </h2>
          {unseen ? (
            <button
              type="button"
              className="flex min-h-8 items-center gap-1 rounded-sm text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={followLatest}
            >
              <ArrowDown className="h-3 w-3" aria-hidden="true" />
              {intl.formatMessage(messages.activityLatest)}
            </button>
          ) : null}
          <button
            type="button"
            onClick={close}
            className="-me-2 shrink-0 rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={intl.formatMessage(messages.close)}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        {viewingHistory && liveStatus ? (
          <button
            type="button"
            onClick={onViewLive}
            className="mx-6 mt-4 mb-1 flex items-center gap-2 rounded-md border border-border px-3 py-2 text-start text-sm hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="min-w-0 flex-1">
              {intl.formatMessage(messages.activityViewCurrent)}
            </span>
          </button>
        ) : null}
        <p id="stage-activity-request" className="sr-only">
          {request ? splitAttachmentMarker(request).text : intl.formatMessage(messages.home)}
        </p>
        <p role="status" className="sr-only">
          {tools
            .filter((tool) => tool.state === 'preparing' || tool.state === 'running')
            .map((tool) => intl.formatMessage(CURRENT_MESSAGES[tool.kind]))
            .join(' ')}
        </p>
        <section
          ref={scrollRef}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: The trail must be keyboard-scrollable even when no action has links.
          tabIndex={0}
          aria-label={intl.formatMessage(messages.activity)}
          className="stage-activity-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          onScroll={() => {
            const el = scrollRef.current;
            if (el) {
              following.current =
                !inspecting.current && el.scrollHeight - el.clientHeight - el.scrollTop < 24;
              if (following.current) {
                setUnseen(false);
              }
            }
          }}
        >
          {groups.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {liveStatus || intl.formatMessage(messages.activityUnavailable)}
            </p>
          ) : null}
          {groups.map((group) => (
            <ActivityRequest
              key={group.responseId}
              group={group}
              selected={group.responseId === responseId || group.run?.state === 'running'}
              preserveExpanded={!following.current}
              navigableSurfaceIds={navigableSurfaceIds}
              onInspect={() => {
                following.current = false;
                inspecting.current = true;
              }}
              onViewSurface={onViewSurface}
            />
          ))}
        </section>
      </aside>
    );
  },
);
